/**
 * Thin fetch wrapper. Always sends cookies (session lives there).
 * Throws `ApiError` for non-2xx so React Query's `onError` works.
 */
import type { ErrorEnvelope } from 'shared';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;

  constructor(status: number, env: ErrorEnvelope) {
    super(env.message);
    this.status = status;
    this.code = env.code;
    this.details = env.details;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: 'include' };
  if (body !== undefined) {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(path, init);

  if (!res.ok) {
    let env: ErrorEnvelope;
    try {
      env = (await res.json()) as ErrorEnvelope;
    } catch {
      env = { code: 'unknown', message: res.statusText };
    }
    throw new ApiError(res.status, env);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};
